import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, KanbanSquare, Table2, Clock, AlertTriangle, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { PageHeader } from "@/components/ui/PageHeader";
import { GlassCard } from "@/components/ui/GlassCard";
import { GlassInput } from "@/components/ui/GlassInput";
import { NxButton } from "@/components/ui/NxButton";
import { NxBadge } from "@/components/ui/NxBadge";
import { NxAvatar } from "@/components/ui/NxAvatar";
import { Skeleton } from "@/components/ui/Skeleton";
import { apiGet, apiPost, apiPatch } from "@/lib/api";
import { PRIORITY_VARIANT, TICKET_STATUS_VARIANT } from "@/lib/rbac";
import { slaInfo, formatRel } from "@/lib/sla";
import pt from "@/lib/i18n";

const COLS = [
    { key: "aberto", label: pt.tickets.statuses.aberto },
    { key: "em_progresso", label: pt.tickets.statuses.em_progresso },
    { key: "pendente_cliente", label: pt.tickets.statuses.pendente_cliente },
    { key: "resolvido", label: pt.tickets.statuses.resolvido },
];

const PRIORITY_BORDER = {
    low: "var(--info)",
    medium: "var(--copper)",
    high: "var(--copper-dark)",
    critical: "var(--error)",
};

const TicketsPage = () => {
    const navigate = useNavigate();
    const [tickets, setTickets] = useState(null);
    const [view, setView] = useState("kanban");
    const [filterCat, setFilterCat] = useState("");
    const [filterPrio, setFilterPrio] = useState("");
    const [showCreate, setShowCreate] = useState(false);
    const [draggingId, setDraggingId] = useState(null);
    const [customers, setCustomers] = useState([]);
    const [form, setForm] = useState({
        customer_id: "",
        subject: "",
        description: "",
        category: "general",
        priority: "medium",
    });
    const [submitting, setSubmitting] = useState(false);
    const [createErr, setCreateErr] = useState("");

    const load = async () => {
        setTickets(null);
        try {
            const params = new URLSearchParams();
            if (filterCat) params.set("category", filterCat);
            if (filterPrio) params.set("priority", filterPrio);
            params.set("page_size", "200");
            const data = await apiGet(`/tickets?${params}`);
            setTickets(data || []);
        } catch (e) { setTickets([]); }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterCat, filterPrio]);

    useEffect(() => {
        if (!showCreate) return;
        (async () => {
            try {
                const c = await apiGet("/customers?page_size=50");
                setCustomers(c || []);
            } catch (e) { setCustomers([]); }
        })();
    }, [showCreate]);

    const onDrop = async (status, ticketId) => {
        const t = (tickets || []).find((x) => x.id === ticketId);
        if (!t || t.status === status) return;
        // optimistic
        setTickets((arr) => arr.map((x) => (x.id === ticketId ? { ...x, status } : x)));
        try {
            await apiPatch(`/tickets/${ticketId}`, { status });
        } catch (e) {
            load();
        }
    };

    const grouped = (tickets || []).reduce((acc, t) => {
        const k = t.status || "aberto";
        acc[k] = acc[k] || [];
        acc[k].push(t);
        return acc;
    }, {});

    const submitCreate = async (e) => {
        e.preventDefault();
        setCreateErr("");
        if (!form.subject || !form.description) {
            setCreateErr(pt.tickets.validationRequired || "Assunto e descrição são obrigatórios.");
            return;
        }
        setSubmitting(true);
        try {
            await apiPost("/tickets", { ...form, customer_id: form.customer_id || null });
            setShowCreate(false);
            setForm({ customer_id: "", subject: "", description: "", category: "general", priority: "medium" });
            load();
        } catch (err) {
            setCreateErr(err?.response?.data?.errors?.[0]?.message || pt.errors.generic);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div data-testid="tickets-root">
            <PageHeader
                title={pt.tickets.title}
                subtitle={pt.tickets.subtitle}
                right={
                    <div className="flex items-center gap-2">
                        <div className="flex items-center rounded-full border border-[color:var(--glass-border)] p-0.5" data-testid="view-toggle">
                            <button
                                type="button"
                                onClick={() => setView("kanban")}
                                className={"px-3 py-1.5 rounded-full text-xs flex items-center gap-1.5 transition-colors " + (view === "kanban" ? "bg-[color:var(--copper)] text-white" : "text-[color:var(--slate-mid)]")}
                                data-testid="view-kanban"
                            >
                                <KanbanSquare size={14} /> {pt.tickets.view_kanban}
                            </button>
                            <button
                                type="button"
                                onClick={() => setView("table")}
                                className={"px-3 py-1.5 rounded-full text-xs flex items-center gap-1.5 transition-colors " + (view === "table" ? "bg-[color:var(--copper)] text-white" : "text-[color:var(--slate-mid)]")}
                                data-testid="view-table"
                            >
                                <Table2 size={14} /> {pt.tickets.view_table}
                            </button>
                        </div>
                        <NxButton variant="copper" icon={<Plus size={16} />} onClick={() => setShowCreate(true)} testId="new-ticket-button">
                            {pt.tickets.new}
                        </NxButton>
                    </div>
                }
            />

            <GlassCard padding="p-3" className="mb-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <select className="glass-input" value={filterCat} onChange={(e) => setFilterCat(e.target.value)} data-testid="tickets-category-filter">
                        <option value="">{pt.tickets.allCategories}</option>
                        {Object.entries(pt.tickets.categories).map(([k, v]) => (
                            <option key={k} value={k}>{v}</option>
                        ))}
                    </select>
                    <select className="glass-input" value={filterPrio} onChange={(e) => setFilterPrio(e.target.value)} data-testid="tickets-priority-filter">
                        <option value="">{pt.tickets.allPriorities}</option>
                        {Object.keys(PRIORITY_VARIANT).map((p) => (
                            <option key={p} value={p}>{pt.tickets[`priority_${p}`]}</option>
                        ))}
                    </select>
                </div>
            </GlassCard>

            {view === "kanban" ? (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="tickets-kanban">
                    {tickets === null ? (
                        COLS.map((c) => (
                            <GlassCard key={c.key} padding="p-4">
                                <Skeleton height="16px" width="50%" />
                                <div className="mt-4 space-y-2">
                                    <Skeleton height="80px" />
                                    <Skeleton height="80px" />
                                </div>
                            </GlassCard>
                        ))
                    ) : (
                        COLS.map((col) => {
                            const list = grouped[col.key] || [];
                            return (
                                <div
                                    key={col.key}
                                    onDragOver={(e) => e.preventDefault()}
                                    onDrop={() => draggingId && onDrop(col.key, draggingId)}
                                    className="rounded-2xl p-3"
                                    style={{ background: "var(--bg-surface)", backdropFilter: "blur(20px)", border: "1px solid var(--glass-border)" }}
                                    data-testid={`column-${col.key}`}
                                >
                                    <div className="flex items-center justify-between px-2 py-2 mb-2">
                                        <h4 className="font-display text-sm text-[color:var(--slate-deep)]">{col.label}</h4>
                                        <NxBadge variant="neutral">{list.length}</NxBadge>
                                    </div>
                                    <div className="space-y-2 min-h-[80px]">
                                        {list.length === 0 ? (
                                            <p className="text-xs text-[color:var(--slate-light)] text-center py-6">{pt.tickets.empty}</p>
                                        ) : (
                                            list.map((t) => {
                                                const sla = slaInfo(t.sla_deadline);
                                                return (
                                                    <div
                                                        key={t.id}
                                                        draggable
                                                        onDragStart={() => setDraggingId(t.id)}
                                                        onDragEnd={() => setDraggingId(null)}
                                                        onClick={() => navigate(`/tickets/${t.id}`)}
                                                        className="glass-card glass-card-hover p-3 cursor-grab active:cursor-grabbing"
                                                        style={{ borderLeft: `3px solid ${PRIORITY_BORDER[t.priority]}` }}
                                                        data-testid={`ticket-card-${t.id}`}
                                                    >
                                                        <div className="flex items-center justify-between mb-1">
                                                            <p className="font-mono text-[10px] text-[color:var(--copper-dark)]">{t.ticket_number}</p>
                                                            <NxBadge variant={sla.variant}>
                                                                <Clock size={10} /> {sla.label}
                                                            </NxBadge>
                                                        </div>
                                                        <p className="font-body text-sm text-[color:var(--slate-deep)] font-medium leading-snug">{t.subject}</p>
                                                        <div className="mt-2 flex items-center justify-between">
                                                            <span className="font-body text-xs text-[color:var(--slate-mid)] truncate">{t.customer?.full_name || "—"}</span>
                                                            {t.assignee && <NxAvatar name={t.assignee.full_name} size="sm" />}
                                                        </div>
                                                    </div>
                                                );
                                            })
                                        )}
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            ) : (
                tickets === null ? (
                    <GlassCard padding="p-6"><Skeleton height="200px" /></GlassCard>
                ) : (
                    <GlassCard padding="p-0" testId="tickets-table">
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="border-b border-[color:var(--glass-border)]">
                                        {[pt.tickets.col_number, pt.tickets.col_subject, pt.tickets.col_customer, pt.tickets.col_category, pt.tickets.col_priority, pt.tickets.col_status, pt.tickets.col_sla, ""].map((h, i) => (
                                            <th key={i} className="text-left px-5 py-3 font-body text-[11px] uppercase tracking-[0.16em] text-[color:var(--slate-light)] font-medium">{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {tickets.map((t) => {
                                        const sla = slaInfo(t.sla_deadline);
                                        return (
                                            <tr key={t.id} onClick={() => navigate(`/tickets/${t.id}`)} className="border-b border-[color:var(--glass-border-subtle)] hover:bg-[color:var(--copper-subtle)] cursor-pointer">
                                                <td className="px-5 py-3 font-mono text-xs text-[color:var(--copper-dark)]">{t.ticket_number}</td>
                                                <td className="px-5 py-3 font-body text-sm text-[color:var(--slate-deep)]">{t.subject}</td>
                                                <td className="px-5 py-3 font-body text-sm text-[color:var(--slate-mid)]">{t.customer?.full_name || "—"}</td>
                                                <td className="px-5 py-3"><NxBadge variant="neutral">{pt.tickets.categories[t.category]}</NxBadge></td>
                                                <td className="px-5 py-3"><NxBadge variant={PRIORITY_VARIANT[t.priority]}>{pt.tickets[`priority_${t.priority}`]}</NxBadge></td>
                                                <td className="px-5 py-3"><NxBadge variant={TICKET_STATUS_VARIANT[t.status]}>{pt.tickets.statuses[t.status]}</NxBadge></td>
                                                <td className="px-5 py-3"><NxBadge variant={sla.variant}><Clock size={10} /> {sla.label}</NxBadge></td>
                                                <td className="px-5 py-3 font-mono text-[11px] text-[color:var(--slate-light)]">{formatRel(t.created_at)}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </GlassCard>
                )
            )}

            {/* Create modal */}
            <AnimatePresence>
                {showCreate && (
                    <>
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setShowCreate(false)} className="fixed inset-0 bg-black/25 z-40" />
                        <motion.div initial={{ opacity: 0, y: 14, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, scale: 0.97 }} transition={{ duration: 0.25 }} className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-[min(94vw,560px)] glass-elevated p-7" data-testid="create-ticket-modal">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="font-display text-2xl text-[color:var(--slate-deep)]">{pt.tickets.new}</h3>
                                <button type="button" onClick={() => setShowCreate(false)} data-testid="close-create-ticket"><X size={16} /></button>
                            </div>
                            {createErr && <p className="mb-3 text-sm text-[color:var(--error)]">{createErr}</p>}
                            <form onSubmit={submitCreate} className="space-y-3">
                                <div>
                                    <label className="block text-xs uppercase tracking-wide text-[color:var(--slate-mid)] mb-1.5 font-medium">Cliente (opcional)</label>
                                    <select value={form.customer_id} onChange={(e) => setForm({ ...form, customer_id: e.target.value })} className="glass-input" data-testid="new-ticket-customer">
                                        <option value="">{pt.tickets.noCustomer || "Sem cliente associado"}</option>
                                        {customers.map((c) => <option key={c.id} value={c.id}>{c.full_name}</option>)}
                                    </select>
                                </div>
                                <GlassInput label="Assunto" required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} testId="new-ticket-subject" />
                                <div>
                                    <label className="block text-xs uppercase tracking-wide text-[color:var(--slate-mid)] mb-1.5 font-medium">{pt.common.description}</label>
                                    <textarea required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={4} className="glass-input w-full resize-none" data-testid="new-ticket-description" />
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="block text-xs uppercase tracking-wide text-[color:var(--slate-mid)] mb-1.5 font-medium">Categoria</label>
                                        <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="glass-input" data-testid="new-ticket-category">
                                            {Object.entries(pt.tickets.categories).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-xs uppercase tracking-wide text-[color:var(--slate-mid)] mb-1.5 font-medium">Prioridade</label>
                                        <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} className="glass-input" data-testid="new-ticket-priority">
                                            {Object.keys(PRIORITY_VARIANT).map((p) => <option key={p} value={p}>{pt.tickets[`priority_${p}`]}</option>)}
                                        </select>
                                    </div>
                                </div>
                                <div className="flex justify-end gap-2 pt-2">
                                    <NxButton variant="secondary" type="button" onClick={() => setShowCreate(false)}>{pt.common.cancel}</NxButton>
                                    <NxButton variant="copper" type="submit" loading={submitting} testId="submit-new-ticket">Criar Ticket</NxButton>
                                </div>
                            </form>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>
        </div>
    );
};

export default TicketsPage;
